import { get, has } from 'lodash';
import moment from 'moment';
import endpoints from '../../utils/endpoints';
import { formatDate, getReference, getAmount, getSingleDetails, getScheduleDetails } from './common';
import { isPayee } from '../../states/payees/selectors';
import { mapAuthorisersGroup } from '../../utils/authoriserUtils';

const hasStandingOrderUUID = (payment) => has(payment, 'standing_order_uuid');

const getBeneficiaryId = (payment) => ({
	beneficiary_id: get(payment, 'beneficiary_id') || get(payment, 'toAccount'),
});

const getBatchType = (payment) => ({
	batch_type: get(payment, 'batch_type'),
});

const getToAccount = ({ toAccount = '', toAccountDetails = {} }) => {
	const type = get(toAccountDetails, 'type', '');
	if (type === 'credit_card') {
		return { to_account: { account_uuid: toAccount } };
	}
	return {
		to_account: {
			account_number: toAccountDetails.accountNumber,
			sort_code: toAccountDetails.sortCode,
		},
	};
};

const getToAccountWithDisplayName = ({ toAccount = '', toAccountDetails = {} }, isPayingToBenificiary = false) => {
	const type = get(toAccountDetails, 'type', '');
	if (type === 'credit_card') {
		return {
			account_id: toAccount,
			display_name: get(toAccountDetails, 'displayName'),
		};
	}
	if (isPayingToBenificiary) {
		return {
			beneficiary_id: toAccount,
		};
	}
	return {
		account_number: get(toAccountDetails, 'accountNumber', ''),
		sort_code: get(toAccountDetails, 'sortCode', ''),
		display_name: get(toAccountDetails, 'displayName'),
	};
};

const getStandingOrderUUID = (payment) => {
	const hasUUID = hasStandingOrderUUID(payment);
	return hasUUID ? { standing_order_uuid: get(payment, 'standing_order_uuid') } : null;
};

const getPaymentContextUuid = (edit, { context_id: payment_context_uuid }) => edit && { payment_context_uuid };

const getAuthorisersPayload = (authorisationStatus, namedApprovers, groupApprovers) =>
	authorisationStatus === 'OBLIGATE' ? (namedApprovers.approver_id.length ? namedApprovers : groupApprovers) : null;

export const getOneOffPaymentReviewRequest =
	(bankId, isContextIdRequired, isEditMode, paymentCategory, beneficiaryId) => (payment) => {
		const uri = endpoints.authorisation.resolve(bankId, 'default');
		const { x_bpi_version, method } = endpoints.authorisation;
		const date = moment.isMoment(payment.date) ? formatDate(payment.date) : '';
		const beneficiary = beneficiaryId ? { beneficiary_id: beneficiaryId } : getBeneficiaryId(payment);

		return {
			id: payment.id,
			method,
			uri,
			x_bpi_version,
			body: {
				source_id: payment.id,
				account_ids: [payment.fromAccount],
				payment_date: date,
				...beneficiary,
				...getAmount(payment),
				...getStandingOrderUUID(payment),
				...getToAccount(payment),
				...getPaymentContextUuid(isContextIdRequired, payment),
				...(paymentCategory ? { payment_category: paymentCategory } : {}),
				isEditMode: !!isEditMode,
			},
		};
	};

export const getRepeatingPaymentReviewRequest = (bankId, payment, isContextIdRequired, isEditMode) => {
	const date = moment.isMoment(payment.date) ? formatDate(payment.date) : '';

	return {
		source_id: payment.id,
		account_ids: [payment.fromAccount],
		recurrence: true,
		payment_date: date,
		...getToAccount(payment),
		...getAmount(payment),
		...getStandingOrderUUID(payment),
		...getPaymentContextUuid(isContextIdRequired, payment),
		isEditMode: !!isEditMode,
	};
};
export const isPayingToBenificiary = (hasUUID, state, payment) => !hasUUID && isPayee(state, payment.toAccount);

export const getEditMode = ({ method, isEditMode }) => {
	if (method === 'POST') return { isEditMode: !!isEditMode };
	return undefined;
};

export const getFutureDatedPaymentConfirmationRequest = (state, bankId, payment, isEditMode) => {
	const hasUUID = hasStandingOrderUUID(payment);
	const endpoint = hasUUID ? endpoints.updateMandate : endpoints.mandate;
	const uri = endpoint.resolve(bankId, 'default', payment.fromAccount, payment.standing_order_uuid);
	const { x_bpi_version, method } = endpoint;

	const { authorisation } = payment;
	const authorisationStatus = (authorisation && authorisation.status) || null;
	const namedApprovers = { approver_id: get(payment, 'authorisers', []).map((authoriser) => authoriser.id) };
	const authorisersGroup = get(payment, 'authorisersGroup', []);
	const mappedAuthorisersGroup = mapAuthorisersGroup(authorisersGroup);
	const groupApprovers = { approval_group_approver_id: mappedAuthorisersGroup };

	// Check here if 'authorisers' exist we can infer named approvers
	// (or should we use explicit global group approvals toggle value?)
	const authorisersPayload = getAuthorisersPayload(authorisationStatus, namedApprovers, groupApprovers);

	return {
		id: payment.id,
		method,
		uri,
		x_bpi_version,
		body: {
			source_id: payment.id,
			...getSingleDetails(payment),
			...getReference(payment),
			...getToAccountWithDisplayName(payment, isPayingToBenificiary(hasUUID, state, payment)),
			...(!!authorisersPayload && authorisersPayload),
			...getEditMode({ method, isEditMode }),
		},
	};
};

export const getOneOffPaymentConfirmationRequest = (bankId, payment, isEditMode) => {
	const uri = endpoints.transaction.resolve(bankId, 'default', payment.fromAccount);
	const { x_bpi_version, method } = endpoints.transaction;

	const { authorisation } = payment;
	const authorisationStatus = (authorisation && authorisation.status) || null;
	const namedApprovers = { approver_id: get(payment, 'authorisers', []).map((authoriser) => authoriser.id) };
	const authorisersGroup = get(payment, 'authorisersGroup', []);
	const mappedAuthorisersGroup = mapAuthorisersGroup(authorisersGroup);
	const groupApprovers = { approval_group_approver_id: mappedAuthorisersGroup };

	// Check here if 'authorisers' exist we can infer named approvers
	// (or should we use explicit global group approvals toggle value?)
	const authorisersPayload = getAuthorisersPayload(authorisationStatus, namedApprovers, groupApprovers);

	return {
		id: payment.id,
		method,
		uri,
		x_bpi_version,
		body: {
			source_id: payment.id,
			...getBeneficiaryId(payment),
			...getAmount(payment),
			...getReference(payment),
			...(!!authorisersPayload && authorisersPayload),
			isEditMode: !!isEditMode,
		},
	};
};

export const getRepeatingPaymentConfirmationRequest = (state, bankId, payment, isEditMode) => {
	const hasUUID = hasStandingOrderUUID(payment);
	const { authorisation } = payment;
	const authorisationStatus = (authorisation && authorisation.status) || null;
	const namedApprovers = { approver_id: get(payment, 'authorisers', []).map((authoriser) => authoriser.id) };
	const authorisersGroup = get(payment, 'authorisersGroup', []);
	const mappedAuthorisersGroup = mapAuthorisersGroup(authorisersGroup);
	const groupApprovers = { approval_group_approver_id: mappedAuthorisersGroup };

	// Check here if 'authorisers' exist we can infer named approvers
	// (or should we use explicit global group approvals toggle value?)
	const authorisersPayload = getAuthorisersPayload(authorisationStatus, namedApprovers, groupApprovers);

	const result = {
		source_id: payment.id,
		...getReference(payment),
		...getToAccountWithDisplayName(payment, isPayingToBenificiary(hasUUID, state, payment)),
		...getScheduleDetails(payment),
		...(!!authorisersPayload && authorisersPayload),
		isEditMode: !!isEditMode,
	};

	if (payment.standing_order_uuid) {
		return {
			...result,
			standing_order_uuid: payment.standing_order_uuid,
		};
	}

	return result;
};

const getBatchPaymentContextUuid = (isEditMode, batchId) => getPaymentContextUuid(isEditMode, { context_id: batchId });

export const getBatchPaymentReviewRequest = ({
	bankId,
	batchId,
	total,
	payment_category,
	payment,
	filteredPayments,
	isContextIdRequired,
	batchContextId,
	isEditMode,
}) => {
	const uri = endpoints.authorisation.resolve(bankId, 'default');
	const { x_bpi_version } = endpoints.authorisation;
	const payment_date = moment.isMoment(payment.date) ? formatDate(payment.date) : '';

	const accounts = filteredPayments.map((payment) => payment.fromAccount);

	return {
		id: batchId,
		method: 'POST',
		uri,
		x_bpi_version,
		body: {
			source_id: batchId,
			account_ids: accounts,
			amount: {
				value: total,
				currency: 'GBP',
			},
			payment_date,
			payment_category,
			...getBatchPaymentContextUuid(isContextIdRequired, batchContextId),
			isEditMode: !!isEditMode,
		},
	};
};

export const getBatchPaymentConfirmationRequest =
	(bankId, batchId, batchName, authorisers = [], groupAuthorisers = [], authorisation = {}) =>
	(payment) => {
		const uri = endpoints.storeTransaction.resolve(bankId, 'default', payment.fromAccount);
		const { x_bpi_version, method } = endpoints.storeTransaction;

		const { authorisation } = payment;
		const authorisationStatus = (authorisation && authorisation.status) || null;
		const namedApprovers = { approver_id: authorisers.map((authoriser) => authoriser.id) };
		const mappedAuthorisersGroup = mapAuthorisersGroup(groupAuthorisers);
		const groupApprovers = { approval_group_approver_id: mappedAuthorisersGroup };

		// Check here if 'authorisers' exist we can infer named approvers
		// (or should we use explicit global group approvals toggle value?)
		const authorisersPayload = getAuthorisersPayload(authorisationStatus, namedApprovers, groupApprovers);

		return {
			id: payment.id,
			uri,
			x_bpi_version,
			method,
			body: {
				source_id: payment.id,
				...getBeneficiaryId(payment),
				...getReference(payment),
				batch_id: batchId,
				batch_name: batchName,
				...(!!authorisersPayload && authorisersPayload),
				...getAmount(payment),
				...getBatchType(payment),
			},
		};
	};

export const getFutureDatedBatchPaymentConfirmationRequest =
	(bankId, batchId, batchName, authorisers, groupAuthorisers = [], state) =>
	(payment) => {
		const hasUUID = hasStandingOrderUUID(payment);
		const endpoint = endpoints.storeSo;
		const uri = endpoint.resolve(bankId, 'default', payment.fromAccount, payment.standing_order_uuid);
		const { x_bpi_version, method } = endpoint;

		return {
			id: payment.id,
			method,
			uri,
			x_bpi_version,
			body: {
				source_id: payment.id,
				...getSingleDetails(payment),
				...getReference(payment),
				...getToAccountWithDisplayName(payment, isPayingToBenificiary(hasUUID, state, payment)),
				batch_id: batchId,
				batch_name: batchName,
				...(groupAuthorisers.length && { approval_group_approver_id: mapAuthorisersGroup(groupAuthorisers) }),
				...getBatchType(payment),
			},
		};
	};
